import {Dimension, Ratio} from './ImageUtils';

type dimensionConfig = {
  [key: string]: Array<string>;
};

const defaultDimensions = 'original';

const branding: dimensionConfig = {
  original: ['200x40', '400x80'],
};

const teamImage: dimensionConfig = {
  original: ['480xauto', '960xauto', '1440xauto', '1920xauto'],
  '16:9': ['256x144', '480x270', '512x288', '768x432', '960x540', '1440x810', '1920x1080'],
};

const player: dimensionConfig = {
  original: ['320xauto', '480xauto', '960xauto', '1440xauto', '1920xauto'],
  '1:1': ['32x32', '64x64', '128x128', '200x200', '300x300'],
  '4:5': ['256x320', '320x400', '480x600', '512x640', '960x1200', '1440x1800', '1920x2400'],
};

const venue: dimensionConfig = {
  '1:1': ['64x64', '128x128', '192x192'],
};

const competition: dimensionConfig = {
  '1:1': ['32x32', '64x64', '96x96', '128x128', '192x192'],
};

const district: dimensionConfig = {
  '1:1': ['32x32', '64x64', '96x96'],
};

const emblem: dimensionConfig = {
  '1:1': ['20x20', '32x32', '40x40', '60x60', '64x64', '96x96', '128x128', '192x192'],
};

const refereeGroup: dimensionConfig = {
  '1:1': ['25x25', '50x50', '100x100', '200x200'],
};

const news: dimensionConfig = {
  original: ['480xauto', '960xauto', '1440xauto', '1920xauto'],
  '1:1': ['64x64', '96x96', '128x128', '192x192'],
  '16:9': ['480x270', '960x540', '1440x810', '1920x1080'],
  '3:2': ['480x320', '960x640', '1440x960', '1920x1280'],
};

const gallery: dimensionConfig = {
  original: ['480xauto', '960xauto', '1440xauto', '1920xauto'],
  '1:1': ['64x64', '128x128', '192x192'],
  '16:9': ['480x270', '960x540', '1440x810', '1920x1080'],
};

const video: dimensionConfig = {
  '1:1': ['64x64', '128x128', '192x192'],
  '16:9': ['320x180', '480x270', '960x540', '1440x810', '1920x1080'],
  original: ['320x180', '480x270', '960x540', '1440x810', '1920x1080'],
};

const matchEvent: dimensionConfig = {
  original: ['320xauto', '480xauto', '960xauto', '1440xauto', '1920xauto'],
  '16:9': ['320x180', '480x270', '960x540', '1440x810', '1920x1080'],
};

const getSizes = (dimensions: string[]): Dimension[] => {
  return dimensions.map((value) => {
    const size = value.split('x');
    const width = size[0] === 'auto' ? 'auto' : parseInt(size[0]);
    const height = size[1] === 'auto' ? 'auto' : parseInt(size[1]);
    return {height, width};
  });
};

const entitySpecs = {
  branding: () => {
    return {
      allowedSizes: getSizes(branding['original']),
      fileTypes: ['svg', 'png'],
    };
  },
  emblem: () => {
    return {
      allowedSizes: getSizes(emblem['1:1']),
      fileTypes: ['svg', 'webp', 'png'],
    };
  },
  news: (ratio?: Ratio) => {
    const dimensions = ratio && ratio in news ? news[ratio] : news[defaultDimensions];
    return {
      allowedSizes: getSizes(dimensions),
      fileTypes: ['svg', 'webp', 'jpeg'],
    };
  },
  gallery: (ratio?: Ratio) => {
    const dimensions = ratio && ratio in gallery ? gallery[ratio] : gallery[defaultDimensions];
    return {
      allowedSizes: getSizes(dimensions),
      fileTypes: ['svg', 'webp', 'jpeg'],
    };
  },
  video: (ratio?: Ratio) => {
    const dimensions = ratio && ratio in video ? video[ratio] : video[defaultDimensions];
    return {
      allowedSizes: getSizes(dimensions),
      fileTypes: ['svg', 'webp', 'jpeg'],
    };
  },
  player: (ratio?: Ratio) => {
    const dimensions = ratio && ratio in player ? player[ratio] : player[defaultDimensions];
    return {allowedSizes: getSizes(dimensions), fileTypes: ['svg', 'webp', 'jpeg']};
  },
  venue: () => {
    return {
      allowedSizes: getSizes(venue['1:1']),
      fileTypes: ['svg', 'webp', 'jpeg'],
    };
  },
  competition: () => {
    return {
      allowedSizes: getSizes(competition['1:1']),
      fileTypes: ['svg', 'webp', 'png', 'jpeg'],
    };
  },
  district: () => {
    return {
      allowedSizes: getSizes(district['1:1']),
      fileTypes: ['svg', 'webp', 'png', 'jpeg'],
    };
  },
  'team-image': (ratio?: Ratio) => {
    const dimensions = ratio && ratio in teamImage ? teamImage[ratio] : teamImage[defaultDimensions];
    return {
      allowedSizes: getSizes(dimensions),
      fileTypes: ['svg', 'webp', 'jpeg'],
    };
  },
  'match-event': (ratio?: Ratio) => {
    const dimensions = ratio && ratio in matchEvent ? matchEvent[ratio] : matchEvent[defaultDimensions];
    return {
      allowedSizes: getSizes(dimensions),
      fileTypes: ['svg', 'webp', 'jpeg'],
    };
  },
  'referee-group': () => {
    return {
      allowedSizes: getSizes(refereeGroup['1:1']),
      fileTypes: ['svg', 'webp', 'png', 'jpeg'],
    };
  },
};

type ImageEntity = keyof typeof entitySpecs;

const getEntitySpec = (entity: ImageEntity, ratio?: Ratio): {allowedSizes: Dimension[]; fileTypes: string[]} =>
  entitySpecs[entity](ratio);

export {getEntitySpec};
export type {ImageEntity};
